/**
 * API Service for GovJobs Portal
 * Handles all backend API calls with proper error handling
 */

import { API_ENDPOINTS } from '../config/api';

const API_TIMEOUT_MS = 10000; // 10 sec - agar API respond na kare toh loading na ghumti rahe

const fetchWithTimeout = (url, opts = {}, ms = API_TIMEOUT_MS) => {
  const ctrl = new AbortController();
  const to = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(to));
};

/**
 * Map backend job format to frontend format
 */
const mapJobFromApi = (job) => ({
  id: parseInt(job.id),
  title: job.job_title || job.post_name,
  department: job.department || 'Not Specified',
  location: job.location || 'All India',
  postDate: job.created_at || new Date().toISOString().split('T')[0],
  notificationDate: job.notification_date || job.notificationDate || '',
  applicationStart: job.application_start || '',
  lastDate: job.application_last || '',
  feePaymentDate: job.fee_payment_date || '',
  correctionDate: job.correction_date || '',
  admitCard: job.admit_card_date || 'Notify Later',
  examDate: job.exam_date || 'Notify Later',
  resultDate: job.result_date || 'Notify Later',
  genOBCEWSFees: job.gen_fee || job.fee_general || '₹0',
  scSTFees: job.sc_st_fee || job.fee_scst || '₹0',
  minimumAge: job.min_age || job.age_min || '18',
  maximumAge: job.max_age || job.age_max || '35',
  totalPosts: job.total_posts || '0',
  eligibilityCriteria: job.eligibility || job.short_description || 'Check notification',
  salary: job.salary || 'As per norms',
  applicationLink: job.apply_link || '',
  notificationLink: job.notification_link || '',
  status: (job.status === 'active' || job.status === 'Active') ? 'Active' : 'Inactive',
  isNew: true,
  urgency: 'high',
  priority: 'High',
  category: job.category || 'General',
  customSections: [],
  templateOverride: null,
  selectedTemplateId: 'default',
  officialWebsite: job.official_website || '',
  advNo: job.adv_no || '',
  shortDescription: job.short_description || '',
  showInTiles: !!(job.show_in_tiles || job.showInTiles),
  tileOrder: parseInt(job.tile_order || job.tileOrder || 0, 10),
  tileDisplayTitle: job.tile_display_title || job.tileDisplayTitle || null,
  showInTicker: !!(job.show_in_ticker || job.showInTicker),
  tickerOrder: parseInt(job.ticker_order || job.tickerOrder || 0, 10),
  createdAt: job.created_at || null,
  post_date_update: job.post_date_update || null,
  postDateUpdate: job.post_date_update || null,
});

/**
 * Fetch ALL data in ONE request - ultra fast (1 round-trip instead of 4)
 */
export const fetchAllData = async () => {
  try {
    const res = await fetchWithTimeout(API_ENDPOINTS.getAll);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error || 'Failed', jobPosts: [], results: [], admitCards: [], answerKeys: [], admissions: [], importants: [] };
    return {
      success: true,
      jobPosts: (json.jobs || []).map(mapJobFromApi),
      results: (json.results || []).map(r => ({ id: r.id, examName: r.exam_name || '', department: r.department || 'Not Specified', resultDate: r.result_date || '', resultLink: r.result_link || '', createdAt: r.created_at || null, post_date_update: r.post_date_update || null, showInTiles: !!(r.show_in_tiles || r.showInTiles), tileOrder: parseInt(r.tile_order || r.tileOrder || 0, 10), tileDisplayTitle: r.tile_display_title || r.tileDisplayTitle || null, showInTicker: !!(r.show_in_ticker || r.showInTicker), tickerOrder: parseInt(r.ticker_order || r.tickerOrder || 0, 10) })),
      admitCards: (json.admit_cards || []).map(a => ({ id: a.id, examName: a.exam_name || '', department: a.department || 'Not Specified', examDate: a.exam_date || '', admitCardLink: a.admit_card_link || '', admitCardReleaseDate: a.admit_card_release_date || null, createdAt: a.created_at || null, post_date_update: a.post_date_update || null, showInTiles: !!(a.show_in_tiles || a.showInTiles), tileOrder: parseInt(a.tile_order || a.tileOrder || 0, 10), tileDisplayTitle: a.tile_display_title || a.tileDisplayTitle || null, showInTicker: !!(a.show_in_ticker || a.showInTicker), tickerOrder: parseInt(a.ticker_order || a.tickerOrder || 0, 10) })),
      answerKeys: (json.answer_keys || []).map(k => ({ id: k.id, examName: k.exam_name || '', department: k.department || 'Not Specified', examDate: k.exam_date || '', answerKeyLink: k.answer_key_link || '', answerKeyReleaseDate: k.answer_key_release_date || null, createdAt: k.created_at || null, post_date_update: k.post_date_update || null, showInTiles: !!(k.show_in_tiles || k.showInTiles), tileOrder: parseInt(k.tile_order || k.tileOrder || 0, 10), tileDisplayTitle: k.tile_display_title || k.tileDisplayTitle || null, showInTicker: !!(k.show_in_ticker || k.showInTicker), tickerOrder: parseInt(k.ticker_order || k.tickerOrder || 0, 10) })),
      admissions: (json.admissions || []).map(ad => ({ id: ad.id, examName: ad.exam_name || '', department: ad.department || 'Not Specified', examDate: ad.exam_date || '', lastDate: ad.last_date || null, admissionLink: ad.admission_link || '', createdAt: ad.created_at || null, post_date_update: ad.post_date_update || null, showInTiles: !!(ad.show_in_tiles || ad.showInTiles), tileOrder: parseInt(ad.tile_order || ad.tileOrder || 0, 10), tileDisplayTitle: ad.tile_display_title || ad.tileDisplayTitle || null, showInTicker: !!(ad.show_in_ticker || ad.showInTicker), tickerOrder: parseInt(ad.ticker_order || ad.tickerOrder || 0, 10) })),
      importants: (json.importants || []).map(mapImportantFromApi)
    };
  } catch (err) {
    console.error('fetchAllData error:', err);
    return { success: false, error: err.name === 'AbortError' ? 'Request timed out' : err.message, jobPosts: [], results: [], admitCards: [], answerKeys: [], admissions: [] };
  }
};

/**
 * Fetch jobs from backend API
 * Supports both JSON format (success/data) and direct array
 */
export const fetchJobs = async () => {
  try {
    const response = await fetchWithTimeout(API_ENDPOINTS.getJobs);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    // Format 1: { success: true, data: [...] }
    if (result.success && Array.isArray(result.data)) {
      return {
        success: true,
        data: result.data.map(mapJobFromApi),
      };
    }

    // Format 2: Direct array [...]
    if (Array.isArray(result)) {
      return {
        success: true,
        data: result.map(mapJobFromApi),
      };
    }

    // Format 3: { jobs: [...] }
    if (Array.isArray(result.jobs)) {
      return {
        success: true,
        data: result.jobs.map(mapJobFromApi),
      };
    }

    throw new Error('Invalid API response format');
  } catch (error) {
    console.error('API fetchJobs error:', error);
    return {
      success: false,
      error: error.name === 'AbortError' ? 'Request timed out' : error.message,
      data: [],
    };
  }
};

// Prefetch cache - hover prefetch = instant load on click (2 min TTL)
const DETAIL_CACHE_TTL = 120000;
const _detailCache = new Map();
const _cacheGet = (key) => {
  const entry = _detailCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.t > DETAIL_CACHE_TTL) { _detailCache.delete(key); return null; }
  return entry.v;
};
const _cacheSet = (key, v) => { _detailCache.set(key, { v, t: Date.now() }); };

const mapJobDetailFromApi = (r) => ({
  id: parseInt(r.id) || r.id,
  seoTitle: r.seo_title || r.seo_title || '',
  metaDescription: r.meta_description || r.meta_description || '',
  metaKeywords: r.meta_keywords || r.meta_keywords || '',
  seoSlug: r.seo_slug || r.seo_slug || '',
  h1Heading: r.h1_heading || r.h1_heading || '',

  title: r.job_title || r.post_name || r.title || '',
  titleAlign: r.extra_fields?.title_align || null,
  titleColor: r.extra_fields?.title_color || null,
  titleSize: r.extra_fields?.title_size || null,
  titleBold: typeof r.extra_fields?.title_bold === 'boolean' ? r.extra_fields.title_bold : null,
  postName: r.post_name || '',
  department: r.department || 'Not Specified',
  totalPosts: r.total_posts || null,
  advNo: r.adv_no || null,
  shortDescription: r.short_description || r.shortInformation || null,
  postDateUpdate: r.post_date_update || null,
  notificationDate: r.notification_date || r.notificationDate || null,
  applicationStart: r.application_start || null,
  applicationLast: r.application_last || null,
  feePaymentDate: r.fee_payment_date || null,
  correctionDate: r.correction_date || null,
  examDate: r.exam_date || null,
  admitCardDate: r.admit_card_date || null,
  resultDate: r.result_date || null,
  answerKeyDate: r.answer_key_date || null,
  feeGeneral: r.fee_general || null,
  feeScst: r.fee_scst || null,
  feePh: r.fee_ph || null,
  portalCharges: r.portal_charges || null,
  paymentMode: r.payment_mode || null,
  ageMin: r.age_min || null,
  ageMax: r.age_max || null,
  ageRelaxation: r.age_relaxation || null,
  applyLink: r.apply_link || r.application_link || null,
  notificationLink: r.notification_link || null,
  officialWebsite: r.official_website || null,
  vacancyDetails: Array.isArray(r.vacancy_details) ? r.vacancy_details : [],
  eligibilityDetails: Array.isArray(r.eligibility_details) ? r.eligibility_details : [],
  howToApply: r.how_to_apply || null,
  importantLinks: Array.isArray(r.important_links) ? r.important_links : [],
  extraFields: (r.extra_fields && typeof r.extra_fields === 'object') ? r.extra_fields : {},
  detailTable: (() => {
    const raw = r.detail_table ?? r.extra_fields?.detail_table;
    if (!raw) return null;
    if (typeof raw === 'string' && raw.trim().startsWith('<')) return raw;
    if (typeof raw === 'object' && (Array.isArray(raw.tables) || (Array.isArray(raw.columns) && Array.isArray(raw.rows)))) return raw;
    try {
      const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (p && Array.isArray(p.tables)) return p;  // multiple tables
      if (p && Array.isArray(p.columns) && Array.isArray(p.rows)) return p;  // single table
      if (Array.isArray(p) && typeof p[0] === 'string') return raw; // MultiBox HTML array
      return null;
    } catch { return null; }
  })(),
});

export const fetchJobById = async (id) => {
  const key = `job:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getJobById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapJobDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchJobById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchJob = (id) => fetchJobById(id);

/**
 * Create new job (when backend supports it)
 */
export const createJob = async (jobData) => {
  try {
    const response = await fetch(API_ENDPOINTS.createJob, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();
    return { success: true, data: result };
  } catch (error) {
    console.error('API createJob error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update job (when backend supports it)
 */
export const updateJob = async (id, jobData) => {
  try {
    const response = await fetch(API_ENDPOINTS.updateJob(id), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jobData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();
    return { success: true, data: result };
  } catch (error) {
    console.error('API updateJob error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete job (when backend supports it)
 */
export const deleteJob = async (id) => {
  try {
    const response = await fetch(API_ENDPOINTS.deleteJob(id), {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('API deleteJob error:', error);
    return { success: false, error: error.message };
  }
};

// ─── Generic fetch helper for array APIs ───
const fetchArrayFromApi = async (url, mapItem, dataKeys = ['data', 'results', 'items']) => {
  try {
    const response = await fetchWithTimeout(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    let items = [];
    if (result.success && Array.isArray(result.data)) {
      items = result.data;
    } else if (Array.isArray(result)) {
      items = result;
    } else {
      for (const key of dataKeys) {
        if (Array.isArray(result[key])) {
          items = result[key];
          break;
        }
      }
    }

    return {
      success: true,
      data: items.map(mapItem),
    };
  } catch (error) {
    console.error('API fetch error:', error);
    return {
      success: false,
      error: error.name === 'AbortError' ? 'Request timed out' : error.message,
      data: [],
    };
  }
};

// ─── Results ───
const mapResultFromApi = (item) => ({
  id: parseInt(item.id) || item.id,
  examName: item.exam_name || item.examName || item.title || '',
  department: item.department || item.organization || 'Not Specified',
  resultDate: item.result_date || item.resultDate || item.date || '',
  resultLink: item.result_link || item.resultLink || item.link || '',
  createdAt: item.created_at || null,
  showInTiles: !!(item.show_in_tiles || item.showInTiles),
  tileOrder: parseInt(item.tile_order || item.tileOrder || 0, 10),
  tileDisplayTitle: item.tile_display_title || item.tileDisplayTitle || null,
  showInTicker: !!(item.show_in_ticker || item.showInTicker),
  tickerOrder: parseInt(item.ticker_order || item.tickerOrder || 0, 10),
  post_date_update: item.post_date_update || null,
  postDateUpdate: item.post_date_update || null,
});

export const fetchResults = async () => {
  return fetchArrayFromApi(
    API_ENDPOINTS.getResults,
    mapResultFromApi,
    ['data', 'results', 'items']
  );
};

const mapResultDetailFromApi = (r) => ({
  id: parseInt(r.id) || r.id,
  seoTitle: r.seo_title || r.seo_title || '',
  metaDescription: r.meta_description || r.meta_description || '',
  metaKeywords: r.meta_keywords || r.meta_keywords || '',
  seoSlug: r.seo_slug || r.seo_slug || '',
  h1Heading: r.h1_heading || r.h1_heading || '',

  examName: r.exam_name || r.examName || r.title || '',
  department: r.department || r.organization || 'Not Specified',
  resultDate: r.result_date || r.resultDate || r.date || '',
  resultLink: r.result_link || r.resultLink || r.link || '',
  postDateUpdate: r.post_date_update || '',
  shortInformation: r.short_information || '',
  applicationBegin: r.application_begin || '',
  lastDateApply: r.last_date_apply || '',
  lastDateFee: r.last_date_fee || '',
  correctionDate: r.correction_date || '',
  examDate: r.exam_date || '',
  admitCardDate: r.admit_card_date || '',
  resultAvailableDate: r.result_available_date || '',
  feeGeneral: r.fee_general || '',
  feeScst: r.fee_scst || '',
  feeFemale: r.fee_female || '',
  paymentMode: r.payment_mode || '',
  ageLimit: r.age_limit || '',
  ageRelaxation: r.age_relaxation || '',
  totalPosts: r.total_posts || '',
  vacancyDetails: Array.isArray(r.vacancy_details) ? r.vacancy_details : [],
  eligibilityDetails: Array.isArray(r.eligibility_details) ? r.eligibility_details : [],
  howToApply: r.how_to_apply || '',
  importantLinks: Array.isArray(r.important_links) ? r.important_links : [],
  applyLink: r.apply_link || r.applyLink || '',
  notificationLink: r.notification_link || r.notificationLink || '',
  officialWebsite: r.official_website || '',
  detailTable: (() => {
    const raw = r.detail_table ?? r.extra_fields?.detail_table;
    if (!raw) return null;
    if (typeof raw === 'string' && raw.trim().startsWith('<')) return raw;
    if (typeof raw === 'object' && (Array.isArray(raw.tables) || (Array.isArray(raw.columns) && Array.isArray(raw.rows)))) return raw;
    try {
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') return raw;
        if (parsed.tables) return parsed.tables;
        if (Array.isArray(parsed.columns)) return [{ columns: parsed.columns, rows: parsed.rows || [] }];
        return parsed;
      }
    } catch (e) {
      if (typeof raw === 'string' && raw.trim().startsWith('<')) return [raw];
    }
    return null;
  })(),
});

export const fetchResultById = async (id) => {
  const key = `result:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getResultById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapResultDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchResultById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchResult = (id) => fetchResultById(id);

export const createResult = async (data) => {
  try {
    const res = await fetch(API_ENDPOINTS.createResult, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error || 'Failed' };
    return { success: true, id: json.id };
  } catch (err) {
    console.error('createResult error:', err);
    return { success: false, error: err.message };
  }
};

// ─── Admit Cards ───
const mapAdmitCardFromApi = (item) => ({
  id: parseInt(item.id) || item.id,
  examName: item.exam_name || item.examName || item.title || '',
  department: item.department || item.organization || 'Not Specified',
  examDate: item.exam_date || item.examDate || item.date || '',
  admitCardLink: item.admit_card_link || item.admitCardLink || '',
  admitCardReleaseDate: item.admit_card_release_date || item.admitCardReleaseDate || null,
  createdAt: item.created_at || null,
  showInTiles: !!(item.show_in_tiles || item.showInTiles),
  tileOrder: parseInt(item.tile_order || item.tileOrder || 0, 10),
  tileDisplayTitle: item.tile_display_title || item.tileDisplayTitle || null,
  showInTicker: !!(item.show_in_ticker || item.showInTicker),
  tickerOrder: parseInt(item.ticker_order || item.tickerOrder || 0, 10),
  post_date_update: item.post_date_update || null,
  postDateUpdate: item.post_date_update || null,
});

const mapAdmitCardDetailFromApi = (r) => ({
  id: parseInt(r.id) || r.id,
  seoTitle: r.seo_title || r.seo_title || '',
  metaDescription: r.meta_description || r.meta_description || '',
  metaKeywords: r.meta_keywords || r.meta_keywords || '',
  seoSlug: r.seo_slug || r.seo_slug || '',
  h1Heading: r.h1_heading || r.h1_heading || '',

  examName: r.exam_name || r.examName || r.title || '',
  department: r.department || r.organization || 'Not Specified',
  examDate: r.exam_date || r.examDate || r.date || '',
  admitCardLink: r.admit_card_link || r.admitCardLink || '',
  applyLink: r.apply_link || r.applyLink || '',
  notificationLink: r.notification_link || r.notificationLink || '',
  admitCardReleaseDate: r.admit_card_release_date || '',
  postDateUpdate: r.post_date_update || '',
  shortInformation: r.short_information || '',
  applicationBegin: r.application_begin || '',
  lastDateApply: r.last_date_apply || '',
  lastDateFee: r.last_date_fee || '',
  correctionDate: r.correction_date || '',
  examDateDetail: r.exam_date_detail || '',
  admitCardAvailable: r.admit_card_available || '',
  applicationsStatusDate: r.applications_status_date || '',
  examCityDate: r.exam_city_date || '',
  feeGeneral: r.fee_general || '',
  feeScst: r.fee_scst || '',
  feePh: r.fee_ph || '',
  paymentMode: r.payment_mode || '',
  ageMin: r.age_min || '',
  ageMax: r.age_max || '',
  ageRelaxation: r.age_relaxation || '',
  totalPosts: r.total_posts || '',
  vacancyDetails: Array.isArray(r.vacancy_details) ? r.vacancy_details : [],
  eligibilityDetails: Array.isArray(r.eligibility_details) ? r.eligibility_details : [],
  howToApply: r.how_to_apply || '',
  importantLinks: Array.isArray(r.important_links) ? r.important_links : [],
  officialWebsite: r.official_website || '',
  admitCardReleaseDate: r.admit_card_release_date || r.admitCardReleaseDate || null,
  detailTable: (() => {
    const raw = r.detail_table ?? r.extra_fields?.detail_table;
    if (!raw) return null;
    if (typeof raw === 'string' && raw.trim().startsWith('<')) return raw;
    if (typeof raw === 'object' && (Array.isArray(raw.tables) || (Array.isArray(raw.columns) && Array.isArray(raw.rows)))) return raw;
    try {
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') return raw;
        if (parsed.tables) return parsed.tables;
        if (Array.isArray(parsed.columns)) return [{ columns: parsed.columns, rows: parsed.rows || [] }];
        return parsed;
      }
    } catch (e) {
      if (typeof raw === 'string' && raw.trim().startsWith('<')) return [raw];
    }
    return null;
  })(),
});

export const fetchAdmitCards = async () => {
  return fetchArrayFromApi(
    API_ENDPOINTS.getAdmitCards,
    mapAdmitCardFromApi,
    ['data', 'admit_cards', 'admitCards', 'items']
  );
};

export const fetchAdmitCardById = async (id) => {
  const key = `admit:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getAdmitCardById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapAdmitCardDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchAdmitCardById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchAdmitCard = (id) => fetchAdmitCardById(id);

export const createAdmitCard = async (data) => {
  try {
    const res = await fetch(API_ENDPOINTS.createAdmitCard, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error || 'Failed' };
    return { success: true, id: json.id };
  } catch (err) {
    console.error('createAdmitCard error:', err);
    return { success: false, error: err.message };
  }
};

// ─── Answer Keys ───
const mapAnswerKeyFromApi = (item) => ({
  id: parseInt(item.id) || item.id,
  examName: item.exam_name || item.examName || item.title || '',
  department: item.department || item.organization || 'Not Specified',
  examDate: item.exam_date || item.examDate || item.date || '',
  answerKeyLink: item.answer_key_link || item.answerKeyLink || '',
  answerKeyReleaseDate: item.answer_key_release_date || item.answerKeyReleaseDate || null,
  createdAt: item.created_at || null,
  showInTiles: !!(item.show_in_tiles || item.showInTiles),
  tileOrder: parseInt(item.tile_order || item.tileOrder || 0, 10),
  tileDisplayTitle: item.tile_display_title || item.tileDisplayTitle || null,
  showInTicker: !!(item.show_in_ticker || item.showInTicker),
  tickerOrder: parseInt(item.ticker_order || item.tickerOrder || 0, 10),
  post_date_update: item.post_date_update || null,
  postDateUpdate: item.post_date_update || null,
});

const mapAnswerKeyDetailFromApi = (r) => ({
  id: parseInt(r.id) || r.id,
  seoTitle: r.seo_title || r.seo_title || '',
  metaDescription: r.meta_description || r.meta_description || '',
  metaKeywords: r.meta_keywords || r.meta_keywords || '',
  seoSlug: r.seo_slug || r.seo_slug || '',
  h1Heading: r.h1_heading || r.h1_heading || '',

  examName: r.exam_name || r.examName || r.title || '',
  department: r.department || r.organization || 'Not Specified',
  examDate: r.exam_date || r.examDate || r.date || '',
  answerKeyLink: r.answer_key_link || r.answerKeyLink || '',
  applyLink: r.apply_link || r.applyLink || '',
  postDateUpdate: r.post_date_update || '',
  shortInformation: r.short_information || '',
  applicationBegin: r.application_begin || '',
  lastDateApply: r.last_date_apply || '',
  lastDateFee: r.last_date_fee || '',
  correctionDate: r.correction_date || '',
  examDateDetail: r.exam_date_detail || '',
  answerKeyAvailable: r.answer_key_available || '',
  applicationsStatusDate: r.applications_status_date || '',
  examCityDate: r.exam_city_date || '',
  feeGeneral: r.fee_general || '',
  feeScst: r.fee_scst || '',
  feePh: r.fee_ph || '',
  paymentMode: r.payment_mode || '',
  ageMin: r.age_min || '',
  ageMax: r.age_max || '',
  ageRelaxation: r.age_relaxation || '',
  totalPosts: r.total_posts || '',
  vacancyDetails: Array.isArray(r.vacancy_details) ? r.vacancy_details : [],
  eligibilityDetails: Array.isArray(r.eligibility_details) ? r.eligibility_details : [],
  howToApply: r.how_to_apply || '',
  importantLinks: Array.isArray(r.important_links) ? r.important_links : [],
  officialWebsite: r.official_website || '',
  answerKeyReleaseDate: r.answer_key_release_date || r.answerKeyReleaseDate || null,
  detailTable: (() => {
    const raw = r.detail_table ?? r.extra_fields?.detail_table;
    if (!raw) return null;
    if (typeof raw === 'string' && raw.trim().startsWith('<')) return raw;
    if (typeof raw === 'object' && (Array.isArray(raw.tables) || (Array.isArray(raw.columns) && Array.isArray(raw.rows)))) return raw;
    try {
      if (typeof raw === 'string') {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') return raw;
        if (parsed.tables) return parsed.tables;
        if (Array.isArray(parsed.columns)) return [{ columns: parsed.columns, rows: parsed.rows || [] }];
        return parsed;
      }
    } catch (e) {
      if (typeof raw === 'string' && raw.trim().startsWith('<')) return [raw];
    }
    return null;
  })(),
});

export const fetchAnswerKeys = async () => {
  return fetchArrayFromApi(
    API_ENDPOINTS.getAnswerKeys,
    mapAnswerKeyFromApi,
    ['data', 'answer_keys', 'answerKeys', 'items']
  );
};

export const fetchAnswerKeyById = async (id) => {
  const key = `key:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getAnswerKeyById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapAnswerKeyDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchAnswerKeyById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchAnswerKey = (id) => fetchAnswerKeyById(id);

export const createAnswerKey = async (data) => {
  try {
    const res = await fetch(API_ENDPOINTS.createAnswerKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error || 'Failed' };
    return { success: true, id: json.id };
  } catch (err) {
    console.error('createAnswerKey error:', err);
    return { success: false, error: err.message };
  }
};

// ─── Admissions ───
const mapAdmissionFromApi = (item) => ({
  id: parseInt(item.id) || item.id,
  examName: item.exam_name || item.examName || item.title || '',
  department: item.department || item.organization || 'Not Specified',
  examDate: item.exam_date || item.examDate || item.date || '',
  lastDate: item.last_date || item.lastDate || null,
  admissionLink: item.admission_link || item.admissionLink || '',
  createdAt: item.created_at || null,
  showInTiles: !!(item.show_in_tiles || item.showInTiles),
  tileOrder: parseInt(item.tile_order || item.tileOrder || 0, 10),
  tileDisplayTitle: item.tile_display_title || item.tileDisplayTitle || null,
  showInTicker: !!(item.show_in_ticker || item.showInTicker),
  tickerOrder: parseInt(item.ticker_order || item.tickerOrder || 0, 10),
  post_date_update: item.post_date_update || null,
  postDateUpdate: item.post_date_update || null,
});

export const fetchAdmissions = async () => {
  return fetchArrayFromApi(
    API_ENDPOINTS.getAdmissions,
    mapAdmissionFromApi,
    ['data', 'admissions', 'items']
  );
};

export const fetchAdmissionById = async (id) => {
  const key = `admission:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getAdmissionById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapJobDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchAdmissionById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchAdmission = (id) => fetchAdmissionById(id);


const mapImportantFromApi = (item) => ({
  id: parseInt(item.id) || item.id,
  examName: item.exam_name || item.examName || item.title || '',
  department: item.department || item.organization || 'Not Specified',
  importantDate: item.important_date || item.importantDate || item.date || '',
  lastDate: item.last_date || item.lastDate || null,
  importantLink: item.important_link || item.importantLink || '',
  createdAt: item.created_at || null,
  showInTiles: !!(item.show_in_tiles || item.showInTiles),
  tileOrder: parseInt(item.tile_order || item.tileOrder || 0, 10),
  tileDisplayTitle: item.tile_display_title || item.tileDisplayTitle || null,
  showInTicker: !!(item.show_in_ticker || item.showInTicker),
  tickerOrder: parseInt(item.ticker_order || item.tickerOrder || 0, 10),
  post_date_update: item.post_date_update || null,
  postDateUpdate: item.post_date_update || null,
});

export const fetchImportants = async () => {
  return fetchArrayFromApi(
    API_ENDPOINTS.getImportants,
    mapImportantFromApi,
    ['data', 'importants', 'items']
  );
};
export const fetchImportantById = async (id) => {
  const key = `important:${id}`;
  const cached = _cacheGet(key);
  if (cached) return cached;
  try {
    const res = await fetch(API_ENDPOINTS.getImportantById(id));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json.success || !json.data) return { success: false, data: null };
    const out = { success: true, data: mapJobDetailFromApi(json.data) };
    _cacheSet(key, out);
    return out;
  } catch (err) {
    console.error('fetchImportantById error:', err);
    return { success: false, data: null };
  }
};
export const prefetchImportant = (id) => fetchImportantById(id);
